import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { ExpenseForm } from '@/components/ExpenseForm';
import { Icons } from '@/components/icons';
import { useAuth } from '@/context/AuthContext';
import { useRepositories } from '@/context/RepositoryContext';
import { useExpense } from '@/hooks/useExpense';
import { invalidateExpenses } from '@/hooks/expensesStore';
import type { ExpenseFormData } from '@/lib/types';

export function ExpenseEditRoute() {
  const { expenseId } = useParams();
  const { user } = useAuth();
  const { expenseRepository } = useRepositories();
  const navigate = useNavigate();
  const { expense: rawExpense, loading } = useExpense(expenseId);

  const expense = useMemo<ExpenseFormData | null>(() => {
    if (!rawExpense) return null;
    return {
      id: rawExpense.id,
      transaction_date: rawExpense.transaction_date,
      store_name: rawExpense.store_name || '',
      items: rawExpense.items,
      subtotal: rawExpense.subtotal,
      tax: rawExpense.tax,
      discount: rawExpense.discount ?? 0,
      total: rawExpense.total,
      status: rawExpense.status,
      notes: rawExpense.notes || '',
    };
  }, [rawExpense]);

  if (!expenseId) return <Navigate to="/expenses" replace />;
  if (loading) return <div className="empty"><div className="ttl">Loading receipt…</div></div>;
  if (!expense) {
    return (
      <div className="empty">
        <Icons.Receipt size={26} stroke="var(--faint)" />
        <div className="ttl">Receipt not found</div>
        <button className="btn" onClick={() => navigate('/expenses')}>Back to receipts</button>
      </div>
    );
  }

  return (
    <ExpenseForm
      mode="edit"
      initialData={expense}
      onCancel={() => navigate(`/expenses/${expenseId}`)}
      onSubmit={async (data) => {
        if (!user) throw new Error('You must be signed in.');
        await expenseRepository.updateExpense(expenseId, {
          transaction_date: data.transaction_date,
          store_name: data.store_name || null,
          items: data.items,
          subtotal: data.subtotal,
          tax: data.tax,
          discount: data.discount ?? 0,
          total: data.total,
          status: data.status || 'pending',
          notes: data.notes || null,
        }, user.uid);
        invalidateExpenses();
        navigate(`/expenses/${expenseId}`);
      }}
    />
  );
}
